import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Label,
  Input,
  Button,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from 'reactstrap';
import { useSelector } from 'react-redux';
import {
  ArrowDown,
  ArrowUp,
  Percent,
  Wallet,
  PiggyBank,
  SendHorizontal,
} from 'lucide-react';
import classNames from 'classnames';
import PageHeader from '~/components/Common/PageHeader';
import Loading from '~/components/Loading';
import api from '~/services/api';
// import useRoles from '~/hooks/useRoles';
import { getRole } from '~/util/getRole';
import { formatPrice } from '~/util/format';
import { format, parseISO } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { toast } from 'react-toastify';
import Pagination from '~/components/Pagination';
import DatePicker from '~/components/DatePicker';
import ReactSelect from 'react-select';
import { fileTypes } from '~/util/fileTypes';
import TwoFactorActionModal from '~/components/Modals/TwoFactorActionModal';

const formatBankType = type => {
  if (type === 'checking') return 'Corrente';
  if (type === 'savings') return 'Poupança';
  if (type === 'conta_corrente') return 'Corrente';
  if (type === 'conta_poupanca') return 'Poupança';
  return type || '-';
};

const safeDateTime = iso => {
  try {
    return format(parseISO(iso), 'dd/MM/yyyy HH:mm', { locale: ptBR });
  } catch {
    return '-';
  }
};

const normalizeAmountParam = value => {
  if (value == null) return '';
  let s = String(value).trim();
  if (!s) return '';
  s = s.replace(/\s+/g, '');
  if (s.includes(',')) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else {
    s = s.replace(/[^0-9.]/g, '');
    const firstDot = s.indexOf('.');
    if (firstDot !== -1) {
      const head = s.slice(0, firstDot + 1);
      const tail = s.slice(firstDot + 1).replace(/\./g, '');
      s = head + tail;
    }
  }
  if (s.endsWith('.')) s = s.slice(0, -1);
  if (s === '') return '';
  return s;
};

const moneyStrToNumber = s => {
  if (s == null) return 0;
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};

const mapMetaToPagination = meta => {
  if (!meta) return null;
  const current_page = meta.current_page ?? meta.page ?? 1;
  const per_page = meta.per_page ?? meta.perPage ?? 15;
  const total = meta.total ?? meta.count ?? 0;
  return { ...meta, current_page, per_page, total };
};

const formatTransferAmount = tr => {
  if (tr.amount != null) return formatPrice((tr.amount || 0) / 100);
  if (tr.amount_cents != null) return formatPrice((tr.amount_cents || 0) / 100);
  return formatPrice(0);
};

const ReceiverTransactions = ({ match }) => {
  const { receiver_id } = match.params;
  const roles = useSelector(({ user }) => user.profile.roles);
  // const { isSupport } = useRoles();

  const [activeTab, setActiveTab] = useState('payables'); // 'payables' | 'transfers'

  const [pageLoading, setPageLoading] = useState(true);
  const [loadingPayables, setLoadingPayables] = useState(false);
  const [loadingTransfers, setLoadingTransfers] = useState(false);
  const [transferring, setTransferring] = useState(false);

  const [receiver, setReceiver] = useState(null);
  const [store, setStore] = useState(null);
  const [bank, setBank] = useState(null);
  const [balances, setBalances] = useState(null);

  const [totals, setTotals] = useState(null);
  const [transactions, setTransactions] = useState([]);

  const [pagination, setPagination] = useState(null);

  const [transfers, setTransfers] = useState([]);
  const [transfersPagination, setTransfersPagination] = useState({
    page: 1,
    perPage: 15,
    total: 0,
  });

  const [transferAmountMasked, setTransferAmountMasked] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [show2FA, setShow2FA] = useState(false);
  const [actionToken, setActionToken] = useState(null);

  const formatBRL = valueNumber =>
    (Number.isFinite(valueNumber) ? valueNumber : 0).toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    });

  const unmaskBRLToNumber = masked => {
    if (!masked) return 0;
    const digits = String(masked).replace(/\D/g, '');
    if (!digits) return 0;
    return Number(digits) / 100;
  };

  const handleTransferAmountMaskedChange = e => {
    const { value } = e.target;
    const digits = value.replace(/\D/g, '');
    const cents = digits ? parseInt(digits, 10) : 0;
    const number = cents / 100;
    setTransferAmountMasked(formatBRL(number));
  };

  const parsedTransferAmount = useMemo(
    () => unmaskBRLToNumber(transferAmountMasked),
    [transferAmountMasked]
  );

  const isAmountValid = useMemo(() => {
    const maxWithdraw =
      balances?.available_for_withdraw ?? balances?.available ?? 0;
    return parsedTransferAmount > 0 && parsedTransferAmount <= maxWithdraw;
  }, [
    parsedTransferAmount,
    balances?.available_for_withdraw,
    balances?.available,
  ]);

  const handle2FASuccess = useCallback(token => {
    setActionToken(token);
    setShow2FA(false);
    setConfirmOpen(true);
  }, []);

  const closeConfirmTransfer = () => setConfirmOpen(false);

  const [filters, setFilters] = useState({
    created_since: '',
    created_until: '',
    status: '',
    type: '',
    amount: '',
  });
  const filtersRef = useRef(filters);
  useEffect(() => {
    filtersRef.current = filters;
  }, [filters]);

  const [showExportModal, setShowExportModal] = useState(false);
  const [optionsReport, setOptionsReport] = useState({
    export: true,
    file_type: 'xlsx',
    // status: undefined,
    // type: undefined,
    // dateStart/dateFinish definidos pelos DatePickers
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const role = useMemo(
    () =>
      getRole(roles, [
        'administrator',
        'digital-consultant',
        'cross-manager',
        'salesman',
        'store-manager',
        'financier',
      ]),
    [roles]
  );

  const formatStatus = status => {
    switch (status) {
      case 'paid':
        return 'Pago';
      case 'waiting_funds':
      case 'pending_transfer':
        return 'Aguardando';
      case 'failed':
        return 'Falha';
      case 'processing':
        return 'Processando';
      case 'confirmed':
        return 'Confirmado';
      case 'prepaid':
        return 'Pré-Pago';
      case 'suspended':
        return 'Suspenso';
      case 'cancelled':
        return 'Cancelado';
      case 'transferred':
        return 'Transferido';
      default:
        return status || '-';
    }
  };

  const formatType = type => {
    switch (type) {
      case 'credit':
        return 'Crédito';
      case 'refund':
        return 'Estorno';
      case 'refund_reversal':
        return 'Estorno de Reembolso';
      case 'chargeback':
        return 'Chargeback';
      case 'chargeback_refund':
        return 'Chargeback Estornado';
      case 'ted':
        return 'TED';
      case 'block':
        return 'Bloqueado';
      case 'unblock':
        return 'Desbloqueado';
      default:
        return type || '-';
    }
  };

  // Options Modal Exportação
  const statusOptions = useMemo(
    () => [
      { value: 'paid', label: 'Pago' },
      { value: 'waiting_funds', label: 'Aguardando' },
      { value: 'failed', label: 'Falha' },
      { value: 'processing', label: 'Processando' },
      { value: 'confirmed', label: 'Confirmado' },
      { value: 'prepaid', label: 'Pré-Pago' },
      { value: 'suspended', label: 'Suspenso' },
      { value: 'cancelled', label: 'Cancelado' },
    ],
    []
  );

  const typeOptions = useMemo(
    () => [
      { value: 'credit', label: 'Crédito' },
      { value: 'refund', label: 'Estorno' },
      { value: 'chargeback', label: 'Chargeback' },
      { value: 'chargeback_refund', label: 'Chargeback Estornado' },
      { value: 'refund_reversal', label: 'Estorno de Reembolso' },
      { value: 'block', label: 'Bloqueado' },
      { value: 'unblock', label: 'Desbloqueado' },
      { value: 'ted', label: 'TED' },
    ],
    []
  );

  const payablesUrl = useMemo(
    () => `/${role}/recipients/${receiver_id}/payables`,
    [role, receiver_id]
  );

  const recipientUrl = useMemo(
    () => `/${role}/recipients/${receiver_id}`,
    [role, receiver_id]
  );
  const transfersUrl = useMemo(
    () => `/${role}/recipients/${receiver_id}/transfers`,
    [role, receiver_id]
  );
  const transferActionUrl = transfersUrl;

  const getURL = useMemo(() => payablesUrl, [payablesUrl]);

  const params = useMemo(() => {
    return {
      page: pagination?.current_page > 1 ? pagination?.current_page : null,
      created_since: filters.created_since || null,
      created_until: filters.created_until || null,
      status: filters.status || null,
      type: filters.type || null,
      amount: normalizeAmountParam(filters.amount) || null,
    };
  }, [pagination, filters]);

  const fetchRecipient = useCallback(async () => {
    try {
      const { data } = await api.get(recipientUrl);
      const r = data?.recipient;
      setReceiver(r || null);
      setStore(r?.store || null);
      setBank(r?.default_bank_account || null);
      if (r?.balance) {
        setBalances({
          available: moneyStrToNumber(r.balance.available_amount),
          to_receive: moneyStrToNumber(r.balance.waiting_funds_amount),
          transferred: moneyStrToNumber(r.balance.transferred_amount),
          transfer_fee: moneyStrToNumber(r.balance.transfer_fee),
          available_for_withdraw: moneyStrToNumber(
            r.balance.available_for_withdraw
          ),
        });
      } else {
        setBalances(null);
      }
    } catch (err) {
      console.error('Erro ao carregar recebedor:', err);
      toast.error('Não foi possível carregar os dados do recebedor.');
    }
  }, [recipientUrl]);

  const setPayablesData = useCallback(data => {
    setTransactions(data?.payables || []);
    setTotals(data?.totals || null);
    setPagination(mapMetaToPagination(data?.meta));
  }, []);

  const manualFetchPayables = useCallback(
    async (overrideFilters, resetToFirstPage = true) => {
      setLoadingPayables(true);
      try {
        const f = overrideFilters ?? filtersRef.current;

        let pageParam = null;
        if (resetToFirstPage) {
          pageParam = 1;
        } else if (pagination?.current_page > 1) {
          pageParam = pagination.current_page;
        }

        const p = {
          page: pageParam,
          created_since: f.created_since || null,
          created_until: f.created_until || null,
          status: f.status || null,
          type: f.type || null,
          amount: normalizeAmountParam(f.amount) || null,
        };

        const { data } = await api.get(getURL, { params: p });
        setPayablesData(data);
      } catch (err) {
        console.error('Erro ao carregar recebíveis:', err);
        toast.error('Não foi possível carregar os recebíveis.');
      } finally {
        setLoadingPayables(false);
      }
    },
    [getURL, pagination?.current_page, setPayablesData]
  );

  const fetchTransfers = useCallback(async () => {
    setLoadingTransfers(true);
    try {
      const { data } = await api.get(transfersUrl, {
        params: {
          page: transfersPagination.page,
          per_page: transfersPagination.perPage,
        },
      });
      const list = Array.isArray(data) ? data : data?.transfers || [];
      setTransfers(list);
      if (data?.meta) setTransfersPagination(t => data.meta || t);
    } catch (err) {
      const msg = err?.response?.data?.message || '';
      if (
        err?.response?.status === 500 &&
        /Nenhuma transferência encontrada/i.test(msg)
      ) {
        setTransfers([]);
      } else {
        toast.error('Não foi possível carregar as transferências.');
      }
    } finally {
      setLoadingTransfers(false);
    }
  }, [transfersUrl, transfersPagination.page, transfersPagination.perPage]);

  useEffect(() => {
    (async () => {
      setPageLoading(true);
      await Promise.all([fetchRecipient()]);
      setPageLoading(false);
    })();
  }, [fetchRecipient]);

  const didInitPay = useRef(false);
  useEffect(() => {
    if (didInitPay.current) return;
    didInitPay.current = true;
    manualFetchPayables(undefined, true);
  }, [manualFetchPayables]);

  const handleSelectChange = e => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    manualFetchPayables(newFilters, true);
  };

  const handleDateChange = e => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [prev.name]: value, [name]: value }));
  };

  const handleDateBlur = e => {
    const { name, value } = e.target;
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    if ((value && value.length === 10) || value === '') {
      manualFetchPayables(newFilters, true);
    }
  };

  const handleDateKeyDown = e => {
    if (e.key === 'Enter') {
      const { name, value } = e.target;
      const newFilters = { ...filters, [name]: value };
      setFilters(newFilters);
      if ((value && value.length === 10) || value === '') {
        manualFetchPayables(newFilters, true);
      }
    }
  };

  const handleAmountChange = e => {
    const { value } = e.target;
    setFilters(prev => ({ ...prev, amount: value }));
  };

  const handleAmountBlur = () => {
    const value = filters.amount;
    const newFilters = { ...filters, amount: value };
    setFilters(newFilters);
    manualFetchPayables(newFilters, true);
  };

  const handleAmountKeyDown = e => {
    if (e.key === 'Enter') {
      const value = filters.amount;
      const newFilters = { ...filters, amount: value };
      setFilters(newFilters);
      manualFetchPayables(newFilters, true);
    }
  };

  const handleExport = async () => {
    try {
      toast.info(
        'Estamos gerando seu relatório, e ele será enviado para o seu e-mail.'
      );

      await api.get(`/${role}/recipients/${receiver.id}/payables/export`, {
        params: optionsReport,
        responseType: 'blob',
      });

      setStartDate(null);
      setEndDate(null);

      setOptionsReport({
        export: true,
        file_type: 'xlsx',
      });

      setShowExportModal(false);
      toast.success('Relatório gerado, já deve constar no seu e-mail.');
    } catch (err) {
      if (err.response && err?.response?.data?.message) {
        toast.error(err?.response?.data?.message, {
          autoClose: err?.response?.data?.duration || 5000,
        });
      } else {
        toast.error('Erro ao gerar relatório');
      }
    }
  };

  const openTransfersTab = useCallback(() => {
    setActiveTab('transfers');
    fetchTransfers();
  }, [fetchTransfers]);

  const handleConfirmTransfer = async () => {
    if (!balances?.available || balances.available <= 0) return;

    if (!isAmountValid) {
      toast.error(
        'Informe um valor válido menor ou igual ao saldo disponível.'
      );
      return;
    }

    try {
      setTransferring(true);
      const amount = Number(parsedTransferAmount.toFixed(2));
      await api.post(transferActionUrl, {
        amount,
        action_token: actionToken,
      });
      await Promise.all([fetchRecipient(), fetchTransfers()]);
      toast.success('Transferência solicitada com sucesso!');
      setConfirmOpen(false);
      setTransferAmountMasked('');
      setActionToken(null);
    } catch (err) {
      console.error('Erro ao transferir:', err);
      const message =
        err?.response?.data?.message ||
        err?.data?.message ||
        err?.message ||
        'Não foi possível realizar a transferência.';

      toast.error(message);
    } finally {
      setTransferring(false);
    }
  };

  const openConfirmTransfer = useCallback(async () => {
    try {
      const { data } = await api.get(
        `/${role}/recipients/${receiver_id}/balance`
      );

      const newBalances = {
        available: moneyStrToNumber(data?.available_amount),
        to_receive: moneyStrToNumber(data?.waiting_funds_amount),
        transferred: moneyStrToNumber(data?.transferred_amount),
        transfer_fee: moneyStrToNumber(data?.transfer_fee),
        available_for_withdraw: moneyStrToNumber(data?.available_for_withdraw),
      };

      setBalances(newBalances);

      if (newBalances.available_for_withdraw <= 0) {
        toast.error('Não há saldo disponível para transferência no momento.');
        return;
      }

      setTransferAmountMasked(formatBRL(newBalances.available_for_withdraw));
      setShow2FA(true);
    } catch (err) {
      console.error('Erro ao consultar saldo:', err);
      toast.error(
        'Não foi possível consultar o saldo atualizado. Tente novamente.'
      );
    }
  }, [role, receiver_id]);

  const renderBankSection = () => {
    if (pageLoading) return <Loading />;
    if (bank) {
      const bankCode = bank.bank || bank.bank_code || bank.bank_code_number;
      const branch = bank.branch_number || bank.agencia;
      const account = bank.account_number || bank.conta;
      const accountDv = bank.account_check_digit || bank.conta_dv;
      const holderDoc = bank.holder_document || bank.document_number;
      const holderName = bank.holder_name || bank.legal_name;

      return (
        <Row className="g-3">
          <Col md={3}>
            <div className="text-muted">Banco</div>
            <div className="fw-semibold">{bankCode || '-'}</div>
          </Col>
          <Col md={2}>
            <div className="text-muted">Agência</div>
            <div className="fw-semibold">{branch || '-'}</div>
          </Col>
          <Col md={3}>
            <div className="text-muted">Conta</div>
            <div className="fw-semibold">
              {account || '-'}
              {accountDv ? `-${accountDv}` : ''}
            </div>
          </Col>
          <Col md={2}>
            <div className="text-muted">Tipo</div>
            <div className="fw-semibold">{formatBankType(bank.type)}</div>
          </Col>
          <Col md={2}>
            <div className="text-muted">Documento</div>
            <div className="fw-semibold">{holderDoc || '-'}</div>
          </Col>
          <Col md={4} className="mt-4">
            <div className="text-muted">Titular</div>
            <div className="fw-semibold">{holderName || '-'}</div>
          </Col>
        </Row>
      );
    }
    return <div className="text-muted">Sem dados bancários cadastrados.</div>;
  };

  return (
    <div className="page-content">
      <Container className="container new-layout">
        <PageHeader
          pageTitle="Movimentações do Recebedor"
          backTo="/recebedores-split"
          responsivePosition="start"
          desktopPosition="center"
        />
        <Card className="card new-layout mb-3">
          <CardBody className="new-layout">
            {pageLoading ? (
              <Loading />
            ) : (
              <Row className="g-3 align-items-center">
                <Col md={4}>
                  <div>
                    <div className="text-muted">Recebedor</div>
                    <h6 className="mb-0">
                      {receiver?.name || receiver?.display_name || '-'}
                    </h6>
                  </div>
                </Col>
                <Col md={4}>
                  <div>
                    <div className="text-muted">Loja</div>
                    <h6 className="mb-0">
                      {store?.fantasy_name || store?.name || '-'}
                    </h6>
                  </div>
                </Col>
                <Col md={4}>
                  <div>
                    <div className="text-muted">Telefone</div>
                    <h6 className="mb-0">
                      {receiver?.telephone || receiver?.phone || '-'}
                    </h6>
                  </div>
                </Col>
              </Row>
            )}
          </CardBody>
        </Card>
        <Card className="card new-layout mb-4">
          <CardBody className="new-layout">
            <div className="d-flex align-items-center gap-2 mb-3">
              <h6 className="mb-0">Informações Bancárias</h6>
            </div>
            {renderBankSection()}
          </CardBody>
        </Card>
        <Card className="card new-layout mb-4">
          <CardBody className="new-layout">
            {totals && (
              <Row className="gy-3 gx-4 align-items-center mb-2">
                <Col xs={6} md={3}>
                  <div className="small text-muted d-flex align-items-center gap-1">
                    <ArrowDown size={16} className="text-success mr-2" />
                    Total de Entradas
                  </div>
                  <div className="h5 mb-0 text-success">
                    {formatPrice(totals.income)}
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="small text-muted d-flex align-items-center gap-1">
                    <ArrowUp size={16} className="text-danger mr-2" />
                    Total de Saídas
                  </div>
                  <div className="h5 mb-0 text-danger">
                    {formatPrice(totals.expenses)}
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="small text-muted d-flex align-items-center gap-1">
                    <Percent size={16} className="text-warning mr-2" />
                    Taxas Pagar.me
                  </div>
                  <div className="h5 mb-0 text-warning">
                    {formatPrice(totals.fees)}
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="small text-muted d-flex align-items-center gap-1">
                    <Wallet size={16} className="text-primary mr-2" />
                    Total Líquido
                  </div>
                  <div className="h5 mb-0 text-primary">
                    {formatPrice(totals.net)}
                  </div>
                </Col>
              </Row>
            )}
            <hr className="my-3" />
            {balances && (
              <Row className="gy-3 gx-4 align-items-center">
                <Col xs={6} md={3}>
                  <div className="small text-muted d-flex align-items-center gap-1">
                    <PiggyBank size={16} className="text-info mr-2" />
                    Total a Receber
                  </div>
                  <div className="h5 mb-0 text-info">
                    {formatPrice(balances.to_receive || 0)}
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="small text-muted d-flex align-items-center gap-1">
                    <PiggyBank size={16} className="text-success mr-2" />
                    Total Disponível
                  </div>
                  <div className="h5 mb-0 text-success">
                    {formatPrice(balances.available || 0)}
                  </div>
                </Col>
                <Col xs={6} md={3}>
                  <div className="small text-muted d-flex align-items-center gap-1">
                    <PiggyBank size={16} className="text-secondary mr-2" />
                    Total Transferido
                  </div>
                  <div className="h5 mb-0 text-secondary">
                    {formatPrice(balances.transferred || 0)}
                  </div>
                </Col>
                <Col xs={12} md="auto" className="ms-md-auto text-md-end">
                  <Button
                    color="primary"
                    className="px-4"
                    disabled={
                      transferring ||
                      !balances.available ||
                      balances.available <= 0
                    }
                    onClick={openConfirmTransfer}
                  >
                    <SendHorizontal size={16} className="me-2" />
                    {transferring ? 'Processando...' : 'Transferir'}
                  </Button>
                </Col>
              </Row>
            )}
          </CardBody>
        </Card>
        <Card className="card new-layout">
          <CardBody className="card-body new-layout">
            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  className={classNames({ active: activeTab === 'payables' })}
                  onClick={() => setActiveTab('payables')}
                  role="button"
                >
                  Recebíveis
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={classNames({ active: activeTab === 'transfers' })}
                  onClick={openTransfersTab}
                  role="button"
                >
                  Transferências
                  {!!transfersPagination?.total && (
                    <Badge color="light" className="ms-2">
                      {transfersPagination.total}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
            </Nav>
            <TabContent activeTab={activeTab}>
              <TabPane tabId="payables">
                <Card className="card new-layout mb-4">
                  <CardBody className="card-body new-layout">
                    <Row className="align-items-end">
                      <Col md={3}>
                        <Label>Data início</Label>
                        <Input
                          type="date"
                          name="created_since"
                          value={filters.created_since}
                          onChange={handleDateChange}
                          onBlur={handleDateBlur}
                          onKeyDown={handleDateKeyDown}
                        />
                      </Col>
                      <Col md={3}>
                        <Label>Data fim</Label>
                        <Input
                          type="date"
                          name="created_until"
                          value={filters.created_until}
                          onChange={handleDateChange}
                          onBlur={handleDateBlur}
                          onKeyDown={handleDateKeyDown}
                        />
                      </Col>
                      <Col md={2}>
                        <Label>Status</Label>
                        <Input
                          type="select"
                          name="status"
                          value={filters.status}
                          onChange={handleSelectChange}
                        >
                          <option value="">Todos</option>
                          <option value="paid">Pago</option>
                          <option value="waiting_funds">Aguardando</option>
                          <option value="failed">Falha</option>
                          <option value="processing">Processando</option>
                          <option value="confirmed">Confirmado</option>
                          <option value="prepaid">Pré-Pago</option>
                          <option value="suspended">Suspenso</option>
                          <option value="cancelled">Cancelado</option>
                        </Input>
                      </Col>
                      <Col md={2}>
                        <Label>Tipo</Label>
                        <Input
                          type="select"
                          name="type"
                          value={filters.type}
                          onChange={handleSelectChange}
                        >
                          <option value="">Todos</option>
                          <option value="credit">Crédito</option>
                          <option value="refund">Estorno</option>
                          <option value="chargeback">Chargeback</option>
                          <option value="chargeback_refund">
                            Chargeback Estornado
                          </option>
                          <option value="refund_reversal">
                            Estorno de Reembolso
                          </option>
                          <option value="block">Bloqueado</option>
                          <option value="unblock">Desbloqueado</option>
                          <option value="ted">TED</option>
                        </Input>
                      </Col>
                      <Col md={2}>
                        <Label>Valor</Label>
                        <Input
                          type="text"
                          name="amount"
                          placeholder="Ex: 76,30 ou 15679,67"
                          value={filters.amount}
                          onChange={handleAmountChange}
                          onBlur={handleAmountBlur}
                          onKeyDown={handleAmountKeyDown}
                          inputMode="decimal"
                        />
                      </Col>
                      {/*
                      <Col md={12} className="mt-3 text-right">
                        <Button
                          color="secondary"
                          onClick={() => setShowExportModal(true)}
                        >
                          Exportar Dados
                        </Button>
                      </Col>
                      */}
                    </Row>
                  </CardBody>
                </Card>
                {loadingPayables ? (
                  <Loading />
                ) : (
                  <>
                    <Table responsive className="new-layout-table">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>ID da Operação</th>
                          <th>ID da Transação</th>
                          <th>Tipo</th>
                          <th>Status</th>
                          <th>Valor</th>
                          <th>Taxa</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map(tx => (
                          <tr key={tx.id}>
                            <td>{safeDateTime(tx.created_at)}</td>
                            <td>{tx.id}</td>
                            <td>{tx.gateway_id}</td>
                            <td>{formatType(tx.type)}</td>
                            <td>{formatStatus(tx.status)}</td>
                            <td>{formatPrice(tx.amount ?? 0)}</td>
                            <td>{formatPrice(tx.fee || 0)}</td>
                          </tr>
                        ))}
                        {!transactions?.length && (
                          <tr>
                            <td
                              colSpan={7}
                              className="text-center text-muted py-4"
                            >
                              Nenhum recebível encontrado.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                    <Row className="w-100 mx-0 mb-4">
                      <Col className="px-0" align="end">
                        {pagination && (
                          <div className="mb-3">
                            <Pagination
                              paginationMeta={pagination}
                              url={getURL}
                              setData={setPayablesData}
                              setLoading={setLoadingPayables}
                              params={params}
                            />
                          </div>
                        )}
                      </Col>
                    </Row>
                  </>
                )}
              </TabPane>
              <TabPane tabId="transfers">
                {loadingTransfers ? (
                  <Loading />
                ) : (
                  <Table responsive className="new-layout-table">
                    <thead>
                      <tr>
                        <th>Data</th>
                        <th>ID da Transferência</th>
                        <th>Status</th>
                        <th>Valor</th>
                        <th>Descrição</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transfers.map(tr => (
                        <tr key={tr.id || tr.transfer_id}>
                          <td>
                            {safeDateTime(
                              tr.created_at ||
                                tr.date_created ||
                                tr.funding_estimated_date
                            )}
                          </td>
                          <td>{tr.id || tr.transfer_id}</td>
                          <td>{formatStatus(tr.status)}</td>
                          <td>{formatTransferAmount(tr)}</td>
                          <td>{tr.description || '-'}</td>
                        </tr>
                      ))}
                      {!transfers?.length && (
                        <tr>
                          <td
                            colSpan={5}
                            className="text-center text-muted py-4"
                          >
                            Nenhuma transferência encontrada.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                )}
              </TabPane>
            </TabContent>
          </CardBody>
        </Card>
        <Modal isOpen={confirmOpen} toggle={closeConfirmTransfer} centered>
          <ModalHeader toggle={closeConfirmTransfer}>
            Confirmar transferência
          </ModalHeader>
          <ModalBody>
            {balances?.available > 0 ? (
              <>
                <p className="mb-2">Informe o valor que deseja transferir.</p>
                <div className="mb-1 small text-muted">
                  Saldo disponível:{' '}
                  <strong>{formatPrice(balances.available)}</strong>
                </div>
                <div className="mb-1 small text-muted">
                  Taxa de transferência:{' '}
                  <strong className="text-danger">
                    - {formatPrice(balances.transfer_fee || 0)}
                  </strong>
                </div>
                <div className="mb-3 small text-muted">
                  Disponível para saque:{' '}
                  <strong className="text-success">
                    {formatPrice(balances.available_for_withdraw || 0)}
                  </strong>
                </div>
                <Label htmlFor="transfer-amount" className="mb-1">
                  Valor
                </Label>
                <Input
                  id="transfer-amount"
                  type="text"
                  value={transferAmountMasked}
                  onChange={handleTransferAmountMaskedChange}
                  placeholder="R$ 0,00"
                  inputMode="numeric"
                  autoFocus
                />
                {!isAmountValid && transferAmountMasked !== '' && (
                  <div className="mt-2 small text-danger">
                    Valor inválido. Use um valor maior que zero e até o
                    disponível para saque.
                  </div>
                )}
              </>
            ) : (
              <>Não há saldo disponível para transferência.</>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={closeConfirmTransfer}
              disabled={transferring}
            >
              Cancelar
            </Button>
            <Button
              color="primary"
              onClick={handleConfirmTransfer}
              disabled={
                transferring ||
                !balances?.available ||
                balances.available <= 0 ||
                !isAmountValid
              }
            >
              {transferring ? 'Processando...' : 'Confirmar'}
            </Button>
          </ModalFooter>
        </Modal>
        <Modal isOpen={showExportModal}>
          <ModalHeader toggle={() => setShowExportModal(false)}>
            Recebíveis e Transferências:{' '}
            {receiver?.name || receiver?.display_name || '-'}
          </ModalHeader>
          <ModalBody>
            <Row className="w-100 mx-0 mb-4 mt-2">
              <Col className="px-0 pr-2">
                <Label>Status da transação</Label>
                <ReactSelect
                  name="status-financier-report"
                  id="status-financier-report"
                  options={statusOptions}
                  classNamePrefix="select"
                  placeholder="Todos"
                  isClearable
                  value={
                    optionsReport.status
                      ? statusOptions.find(
                          p => p.value === optionsReport.status
                        )
                      : null
                  }
                  onChange={option => {
                    setOptionsReport(state => ({
                      ...state,
                      status: option?.value,
                    }));
                  }}
                />
              </Col>
              <Col className="px-0 pl-2">
                <Label>Tipo de transação</Label>
                <ReactSelect
                  name="type-financier-report"
                  id="type-financier-report"
                  options={typeOptions}
                  classNamePrefix="select"
                  placeholder="Todos"
                  isClearable
                  value={
                    optionsReport.type
                      ? typeOptions.find(p => p.value === optionsReport.type)
                      : null
                  }
                  onChange={option => {
                    setOptionsReport(state => ({
                      ...state,
                      type: option?.value,
                    }));
                  }}
                />
              </Col>
            </Row>

            <Row className="w-100 mx-0 my-2">
              <Col className="px-0">
                <Label>
                  Deseja filtrar por um intervalo de datas específico?
                </Label>
                <Row className="w-100 mx-0 mb-2 mb-lg-2 d-flex flex-row align-items-center">
                  <Col xs="6" className="pl-0 pr-2 mb-2 mb-lg-0">
                    <DatePicker
                      value={startDate}
                      onChange={selectedDate => {
                        const date = format(selectedDate, 'yyyy-MM-dd', {
                          locale: ptBR,
                        });
                        setOptionsReport(prev => ({
                          ...prev,
                          dateStart: date,
                        }));
                        setStartDate(selectedDate);
                      }}
                      mask="__/__/___"
                      placeholder="Dia Inicial"
                    />
                  </Col>
                  <Col xs="6" className="pl-0 pr-2 mb-2 mb-lg-0">
                    <DatePicker
                      value={endDate}
                      onChange={selectedDate => {
                        const date = format(selectedDate, 'yyyy-MM-dd', {
                          locale: ptBR,
                        });
                        setOptionsReport(prev => ({
                          ...prev,
                          dateFinish: date,
                        }));
                        setEndDate(selectedDate);
                      }}
                      mask="__/__/___"
                      placeholder="Dia Final"
                    />
                  </Col>
                </Row>
              </Col>
            </Row>

            <Row className="w-100 mx-0 mb-4 mt-2">
              <Col className="px-0">
                <Label>Qual formato de arquivo que deseja no relatório?</Label>
                <ReactSelect
                  name="filetype-financier-report"
                  id="filetype-financier-report"
                  options={fileTypes}
                  classNamePrefix="select"
                  placeholder="Formato de arquivo"
                  isClearable
                  value={fileTypes.find(
                    p => p?.value === optionsReport?.file_type
                  )}
                  onChange={option => {
                    setOptionsReport(state => ({
                      ...state,
                      file_type: option?.value,
                    }));
                  }}
                />
              </Col>
            </Row>
          </ModalBody>
          <ModalFooter>
            <Button
              size="lg"
              color="primary"
              className="new-layout-btn main"
              onClick={handleExport}
            >
              Exportar
            </Button>
          </ModalFooter>
        </Modal>
        <TwoFactorActionModal
          isOpen={show2FA}
          toggle={() => setShow2FA(false)}
          onSuccess={handle2FASuccess}
        />
      </Container>
    </div>
  );
};

export default ReceiverTransactions;
